<template>
  <div class="InsurancePersonalDataStep2Form">
    <h2 class="gl_form-title small mb-5">
      {{ title }}
    </h2>

    <BaseCheckBox
      v-if="showAutocompleteAddressCheck"
      id="same-info"
      v-model="checked"
      :label="t('hiring.common.insurance-personal-data-step-2.check')"
      class="principal-data"
    />

    <PostalCodeComponent
      :key="counterCheck"
      :label="t('hiring.common.insurance-personal-data-step-2.label-postal-code')"
      id="postalCode"
      :placeholder="postalPlaceholder"
      v-model="postalCode"
      :editable="isPostalCodeEditable && !checked"
      :rules="postalCodeRules"
      :inputmode="InputmodeTypes.Numeric"
      @focus="onFocusPostalCode"
      @enter-focus="blockNext"
    />

    <BaseInput
      :label="t('hiring.common.insurance-personal-data-step-2.label-province')"
      id="province"
      :placeholder="provincePlaceholder"
      :modelValue="province"
      :editable="false"
      :rules="[required]"
    />

    <BaseAutocompleteInputStrict
      id="address"
      :label="cityLabel"
      :placeholder="cityPlaceholder"
      :items="cities"
      :editable="!!provinceCode && !checked"
      v-model="city"
      :loading="searching"
      @update="searchCity"
      :rules="[required]"
    />
    <div
      ref="hiddenFieldWrapper"
      style="display: none"
      aria-hidden="true"
    >
      <v-text-field
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="disabledNext"
        :rules="[validateNext]"
      ></v-text-field>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import Insured from '@/modules/common/common/models/APP/Insured'
  import PostalCodeComponent from '@/modules/core/components/formFields/PostalCodeComponent.vue'
  import BaseAutocompleteInputStrict from '@/modules/core/components/ui/BaseAutocompleteInputStrict.vue'
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useCoolDown } from '@/modules/core/composables/useCoolDown'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import BranchEnum from '@/modules/core/models/BranchEnum'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import InputmodeTypes from '@/modules/core/models/InputmodeTypes'
  import type { MDWCityInterface } from '@/modules/core/models/types/middleware'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { validatePostalCode } from '@/utils/validations.utils'
  import { computed, inject, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import AddressDetails from '../../models/APP/AddressDetails'

  const { t } = useI18n()
  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()
  const { required, isNotFromNavarra } = useRules()
  const { runCoolDown } = useCoolDown()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra?: Record<string, any>
  }
  const props: PropsInterface = withDefaults(defineProps<PropsInterface>(), {})

  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )
  const holderInsured = ref(
    props.extra?.usePolicyHolderAsHolder ? props.answers.policyHolder : props.answers.holder
  )
  const currentInsuredIsHolder = ref(currentInsured.isHolder)
  const insuredName = ref(currentInsured.userDetails.name)
  const checked = ref(!currentInsured.addressDetails.editable)
  const counterCheck = ref(0)
  const currentInsuredAddress = currentInsured.addressDetails
  const postalCode = ref(currentInsuredAddress.postalCode)
  const province = ref(currentInsuredAddress.province)
  const city = ref()
  const cityCode = ref(currentInsuredAddress.cityCode)
  const provinceCode = ref<string | null>(null)
  const searching = ref(false)

  const citiesWithCodes = ref<{ city: string; cityCode: string }[]>([])

  let validateBlockNext = inject<() => Promise<boolean>>('validateBlockNext') ?? (async () => true)

  const disabledNext = ref(false)
  const postalCodeRules = computed(() => {
    if (
      props.configurations.businessLine === BranchEnum.HEALTH ||
      props.configurations.businessLine === BranchEnum.FOREIGN_HEALTH
    ) {
      return [required, isNotFromNavarra]
    }

    return [required]
  })
  const isPostalCodeEditable = computed(() => {
    if (
      [BranchEnum.DENTAL, BranchEnum.PETS, BranchEnum.TRAVEL, BranchEnum.LIFE].includes(
        props.configurations.businessLine
      )
    ) {
      return true
    }

    const { addressDetails, role } = currentInsured
    return !addressDetails.postalCode || role === UserRoleEnum.HOLDER_NOT_INSURED
  })
  const showAutocompleteAddressCheck = computed(() => {
    const isHolder = !!currentInsuredIsHolder.value
    if (isHolder) {
      return false
    }

    const postalCodeIsEmpty = !currentInsuredAddress.postalCode
    const areEquals =
      holderInsured.value.addressDetails.postalCode === currentInsuredAddress.postalCode
    return postalCodeIsEmpty || areEquals
  })

  const title = computed(() => {
    const name = insuredName.value
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'insured' : 'holder'

    return t(`hiring.common.insurance-personal-data-step-2.form-legend-${i18n}`, { name })
  })

  const postalPlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-2.placeholder-postal-code.${i18n}`)
  })

  const cityLabel = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-2.label-town.${i18n}`)
  })

  const cityPlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-2.placeholder-town.${i18n}`)
  })

  const provincePlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'
    return t(`hiring.common.insurance-personal-data-step-2.placeholder-province.${i18n}`)
  })

  watch(
    () => checked.value,
    (newValue) => {
      if (checked.value) disabledNext.value = true
      onCheckChange(newValue)
    }
  )
  watch(
    () => city.value,
    (city) => loadCityCode(city!)
  )
  watch(disabledNext, validateBlockNext)

  const cities = computed(() => citiesWithCodes.value.map((c) => c.city))

  async function onCheckChange(checked: boolean) {
    counterCheck.value += 1
    if (!checked) {
      return clearAddressValues()
    }

    fillAddressValues()
  }
  function blockNext() {
    //TODO BLOCK NEXT
    if (!checked.value) disabledNext.value = false
    else disabledNext.value = true
  }
  function validateNext(value: boolean) {
    return value === true
  }
  function clearAddressValues() {
    province.value = currentInsuredAddress.province
    city.value = currentInsuredAddress.city ?? ''
    fillProvince()
  }

  async function fillAddressValues() {
    const provinceCodeData = await findProvinceData(holderInsured.value.addressDetails.postalCode)

    postalCode.value = holderInsured.value.addressDetails.postalCode
    province.value = holderInsured.value.addressDetails.province
    city.value = holderInsured.value.addressDetails.city
    provinceCode.value = provinceCodeData.code
  }

  function onFocusPostalCode(focused: boolean) {
    if (focused) return

    if (!validatePostalCode(postalCode.value!)) return

    fillProvince()
  }

  async function searchCity(city: string) {
    if (!provinceCode.value) {
      citiesWithCodes.value = []
      return
    }

    searching.value = true
    runCoolDown(loadCities, { partialCity: city })
  }

  function loadCityCode(city: string) {
    cityCode.value = citiesWithCodes.value.find((c) => c.city === city)?.cityCode!
  }

  async function loadCities({ partialCity }: { partialCity: string }) {
    if (!provinceCode.value || typeof partialCity !== 'string') {
      searching.value = false
      return
    }

    if (partialCity.length < 2) {
      searching.value = false
      citiesWithCodes.value = []
      return
    }

    const { values, aborted } = await commonStore.findCities(provinceCode.value, partialCity)

    if (aborted) return

    citiesWithCodes.value = optimizeListCityWithAccent(partialCity, values)
    searching.value = aborted
  }

  function optimizeListCityWithAccent(partialCity: string, values: MDWCityInterface[]) {
    /*const regexAccent = /[áéíóúÁÉÍÓÚ]/;

  if(regexAccent.test(partialCity)) {
    values.forEach(value => {
      const pc = partialCity.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      value.city = value.city.split(pc.toUpperCase()).join(partialCity.toUpperCase())
    })
    return values
  }*/
    return values
  }

  async function fillProvince() {
    if (!postalCode.value) return

    const provinceCodeData = await findProvinceData(postalCode.value)
    const provinceChanged =
      provinceCode.value !== null && provinceCode.value !== provinceCodeData.code

    province.value = provinceCodeData.description
    provinceCode.value = provinceCodeData.code

    if (provinceChanged || !city.value) {
      city.value = null
      disabledNext.value = true
    }
  }

  async function findProvinceData(postalCode: string) {
    if (!postalCode) throw new Error('PostalCode is required')

    const prefix = postalCode.substring(0, 2)
    const provinceCodes = await commonStore.fetchProvinceCodes()

    const provinceCodeData = provinceCodes.find((p: any) => p.code === prefix)
    if (!provinceCodeData) throw new Error('Province code not found')

    return provinceCodeData
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    if (checked.value) {
      const insuredCountry = currentInsured.addressDetails.country
      currentInsured.addressDetails = new AddressDetails(holderInsured.value.addressDetails)
      currentInsured.addressDetails.editable = false
      currentInsured.addressDetails.country = insuredCountry
    } else {
      currentInsured.addressDetails.postalCode = postalCode.value
      currentInsured.addressDetails.province = province.value
      currentInsured.addressDetails.city = city.value
      currentInsured.addressDetails.cityCode = cityCode.value
      currentInsured.addressDetails.editable = true
    }

    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : { addressDetails: currentInsured.addressDetails }

    return { page, answers }
  }

  async function beforeMount() {
    loadingStore.startLoading()
    await fillProvince()
    city.value = currentInsuredAddress.city!
    if (city.value) {
      await loadCities({ partialCity: city.value })
      loadCityCode(city.value)
    }
    loadingStore.stopLoading()
  }

  onBeforeMount(beforeMount)

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
</script>

<style lang="scss" scoped>
  .InsurancePersonalDataStep2Form {
    .principal-data {
      margin-top: $spacing-xl;
      margin-bottom: $spacing-xl;
    }
  }
</style>
